import React from 'react'
import {
  AllUsers_users_users,
  useAuth,
  UserRole,
  useAddTestSessionMembers,
  useSubperson,
  useGroupsData,
  GroupMemberRole,
  MemberTypes,
} from '@modmd/data'
import { useToaster } from 'utils/useToaster'
import { FindOrInviteUser } from 'sharedComponents/FindOrInviteUser'
import { CsvInviteNewGroupMembers } from 'sharedComponents/CsvInviteNewGroupMembers'
import Card from 'components/Card'
import { Dialog } from 'components/Dialog'
import Icon from 'components/Icon'
import Button from 'components/Button'
import { Box } from 'components/Layout'
import { ButtonColorVariantType } from 'components/Button/Button'
import { AddNewGroupMembers } from 'sharedComponents/AddNewGroupMembers'
import { Dropdown } from 'components/AutoCompleteDropdown'
import { Loading } from 'components/Loading'
import { LoadingSkeleton } from 'components/Datalist'
import { SelectInput } from 'components/SelectInput'
import { AddMembersFromGroup } from './AddMembersFromGroup'

export type MemberType = 'participant' | 'staff'
type UserIdAndEmail = Array<Pick<AllUsers_users_users, 'id' | 'email'>>

export interface Props {
  testSessionId: string
  buttonLabel: string
  companyId?: string
  groupId?: string
  buttonColorVariant?: ButtonColorVariantType
  onAdd: (user: Array<Pick<AllUsers_users_users, 'id' | 'email'>>) => void
  addMemberType: MemberType | null
  isSearchingInCompanyOnly?: boolean
  hasGroupRoleOnly?: boolean
  multipleGroups?: boolean
  disabledButton?: boolean
}

export const AddNewTestSessionMembers: React.VFC<Props> = ({
  onAdd,
  testSessionId,
  addMemberType,
  buttonLabel,
  buttonColorVariant,
  companyId,
  groupId,
  isSearchingInCompanyOnly = false,
  hasGroupRoleOnly,
  multipleGroups = false,
  disabledButton = false,
}) => {
  const auth = useAuth()
  const isSuperAdmin = auth.data.User.role === UserRole.SUPER_ADMIN
  const isCLS = auth.data.User.role === UserRole.CLS
  const { setToastMessage } = useToaster()
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [selectedGroupId, setSelectedGroupId] = React.useState<string>()

  const { addTestSessionMembersWithInput, addTestSessionMembersData } = useAddTestSessionMembers({
    testSessionId,
    memberType: addMemberType,
    groupId,
  })

  const groupsQuery = useGroupsData({
    filter: {
      companyIds: [companyId!],
      ...(hasGroupRoleOnly && {
        memberUserGroupRoles: [GroupMemberRole.Coordinator, GroupMemberRole.Manager],
        memberUserIds: [auth.data.User.id],
      }),
    },
    pageLength: 999,
    membersPageLength: 0,
    skip: !companyId,
  })
  const groups = groupsQuery.data?.groups?.Groups || []

  const handleAddSuccess = React.useCallback(
    (users: UserIdAndEmail = []) => {
      setToastMessage(`Members added!`, 'success')
      setIsDialogOpen(false)
      onAdd(users)
    },
    [setToastMessage, onAdd]
  )

  const handleAddPatients = React.useCallback<(users: UserIdAndEmail) => void>(
    async (users) => {
      try {
        await addTestSessionMembersWithInput(users.map(({ id }) => id))
        handleAddSuccess(users)
      } catch {
        // ignore
      }
    },
    [addTestSessionMembersWithInput, handleAddSuccess]
  )

  interface UserProps {
    id: string
    firstName: string | null
    lastName: string | null
    email: string | null
  }
  const defaultUser = {
    id: '',
    firstName: null,
    lastName: null,
    email: null,
  }
  const [selectedUser, setSelectedUser] = React.useState<UserProps>(defaultUser)
  const { data: subperson, loading: isSubpersonLoading } = useSubperson({
    userId: selectedUser?.id,
    companyId,
    groupId,
    skip: !selectedUser.id,
  })

  React.useEffect(() => {
    if (
      subperson !== undefined &&
      !subperson?.subperson?.length &&
      selectedUser.id !== defaultUser.id
    ) {
      handleAddPatients([selectedUser])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subperson])

  return (
    <>
      <Button
        colorVariant={buttonColorVariant || 'info'}
        onClick={() => {
          setIsDialogOpen(true)
          setSelectedUser(defaultUser)
        }}
        rightIcon={<Icon.AddPerson />}
        disabled={disabledButton}
      >
        {buttonLabel}
      </Button>
      <Dialog
        isOpen={!!isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
        variant="right"
        maxWidth="450px"
      >
        {companyId && (groupId || multipleGroups) ? (
          <Box>
            <Card.Title onDismiss={() => setIsDialogOpen(false)}>Add patients</Card.Title>
            <Card.Content>
              {groupsQuery.loading ? (
                <Loading isLoading skeletonComp={<LoadingSkeleton columns={2} rows={2} />} />
              ) : (
                multipleGroups && (
                  <SelectInput
                    name="group"
                    label="Group"
                    placeholder="Select group"
                    onChange={(evt) => setSelectedGroupId(evt.target.value)}
                    value={selectedGroupId || ''}
                    hasEmptyValue
                    emptyValueLabel="Select group"
                    options={
                      groups
                        .map(({ id, name }) => ({
                          value: id,
                          label: name,
                        }))
                        .filter(({ value }) => (groupId ? groupId === value : value)) || []
                    }
                  />
                )
              )}

              {(groupId || selectedGroupId) && (
                <Box display="grid" gridGap="0.5rem" justifyContent="end">
                  <AddNewGroupMembers
                    canSetRole={isSuperAdmin || isCLS}
                    buttonLabel="Search and add members"
                    groupId={groupId || selectedGroupId!}
                    companyId={companyId}
                    isSearchingInCompanyOnly={isSearchingInCompanyOnly}
                    onAddSuccess={handleAddPatients}
                  />
                  <CsvInviteNewGroupMembers
                    companyId={companyId}
                    groupId={groupId || selectedGroupId!}
                    onUploadSuccess={handleAddPatients}
                  />
                  <AddMembersFromGroup
                    companyId={companyId}
                    groupId={groupId || selectedGroupId!}
                    testSessionId={testSessionId}
                    onAddSuccess={() => {
                      handleAddSuccess()
                    }}
                  />
                </Box>
              )}
            </Card.Content>
          </Box>
        ) : (
          <Box>
            <Card.Title onDismiss={() => setIsDialogOpen(false)}>
              {addMemberType === MemberTypes.Participant ? 'Add patient' : 'Add staff'}
            </Card.Title>
            <Card.Content>
              <FindOrInviteUser
                placeholder={selectedUser?.email || 'No user selected'}
                onUserSelect={(user) =>
                  addMemberType === 'staff' ? handleAddPatients([user]) : setSelectedUser(user)
                }
                isInvitationEnabled={addMemberType === MemberTypes.Participant}
                isLoadingAddMember={addTestSessionMembersData.loading}
                disabledEditEmail
                isStaff={addMemberType === MemberTypes.Staff}
                buttonLabel={addMemberType === MemberTypes.Participant ? 'Add Member' : 'Add Staff'}
              />
              {addMemberType === MemberTypes.Participant &&
                subperson?.subperson &&
                subperson.subperson.length > 0 && (
                  <Dropdown
                    name="user"
                    value=""
                    isLoading={isSubpersonLoading}
                    options={[
                      {
                        value: selectedUser.id,
                        text: selectedUser.email,
                      },
                      ...subperson?.subperson?.map((e) => ({
                        value: e?.id || 'No value',
                        text: e?.firstName,
                      })),
                    ]}
                    isWithOptionsFilter={false}
                    selectOnNavigation={false}
                    onSelectChange={(
                      _: React.SyntheticEvent<HTMLElement>,
                      data: { value: string }
                    ) => {
                      handleAddPatients([{ ...defaultUser, id: data.value }])
                    }}
                  />
                )}
            </Card.Content>
          </Box>
        )}
      </Dialog>
    </>
  )
}
